/**
 * useSettingsRHF Hook
 *
 * Bridge between React Hook Form + Zod validation and the existing
 * Firestore auto-save mechanism (useSettingsForm).
 *
 * Replaces useSettingsLogic and useFieldErrors with a unified hook that
 * provides:
 * - Zod schema validation via RHF zodResolver
 * - Unified handleChange/handleBlur for all field types
 * - BigNumber total calculation for financial forms
 * - Form object for advanced use (trigger, formState, etc.)
 */

import { useCallback, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import type { FieldValues, UseFormReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { z } from "zod";
import BigNumber from "bignumber.js";
import { useAuth } from "./useAuth";
import { useSettingsForm } from "./useSettingsForm";
import type { SettingsCategory, SettingsData } from "../types/settings";
import type { AppError } from "../types/errors";

export type SettingsFieldValue = number | boolean | string | readonly unknown[];

/** Value types accepted by form handlers — includes null/undefined from cleared inputs */
type SettingsFieldInput = SettingsFieldValue | null | undefined;

export interface UseSettingsRHFResult<T extends SettingsData> {
  formData: Partial<T>;
  handleChange: (field: string, value: SettingsFieldInput) => void;
  handleBlur: (field: string, value: SettingsFieldInput) => void;
  loading: boolean;
  error: AppError | null;
  retry: () => void;
  total: number;
  getError: (field: string) => string | undefined;
  form: UseFormReturn<FieldValues, unknown, unknown>;
}

/**
 * Settings hook with React Hook Form + Zod validation and Firestore auto-save.
 *
 * @param category - Settings category (e.g., 'profile', 'investableAssets')
 * @param schema - Zod schema for validation
 * @param totalFields - Field names to sum for total calculation (optional)
 */
export function useSettingsRHF<T extends SettingsData>(
  category: SettingsCategory,
  schema: z.ZodType<FieldValues>,
  totalFields: (keyof T)[] = []
): UseSettingsRHFResult<T> {
  const { user } = useAuth();

  if (!user?.userId) {
    throw new Error(
      "useSettingsRHF requires an authenticated user (must be used within ProtectedRoute)"
    );
  }

  const {
    formData,
    handleChange: formChange,
    handleBlur: formBlur,
    loading,
    error,
    retry,
  } = useSettingsForm<T>(category, user.userId);

  // RHF for validation — mode "onTouched" shows errors after first interaction.
  // Uses FieldValues (not T) to avoid generic type incompatibilities with zodResolver.
  // Type assertion needed: zodResolver overloads expect concrete Zod types but our
  // generic parameter can't satisfy them statically. Concrete schemas are always passed.
  const form = useForm({
    resolver: zodResolver(schema as Parameters<typeof zodResolver>[0]),
    mode: "onTouched",
    defaultValues: {} as FieldValues,
  });

  // Sync Firestore data → RHF on initial load
  const isInitializedRef = useRef(false);
  useEffect(() => {
    if (
      !loading &&
      !isInitializedRef.current &&
      Object.keys(formData).length > 0
    ) {
      form.reset(formData as FieldValues);
      isInitializedRef.current = true;
    }
  }, [formData, loading, form]);

  // Unified handleChange: update both RHF (for validation) and useSettingsForm (for UI).
  // Accepts null/undefined from form components (cleared fields); Zod validation
  // enforces per-field contracts (e.g., financial fields default to 0 via schema).
  const handleChange = useCallback(
    (field: string, value: SettingsFieldInput) => {
      form.setValue(field, value, {
        shouldValidate: !!form.formState.touchedFields[field],
      });
      formChange(field as keyof T, value);
    },
    [form, formChange]
  );

  // Unified handleBlur: validate via RHF for UI errors, always save to Firestore.
  // Accepts null/undefined from form components (cleared fields); Zod validation
  // enforces per-field contracts.
  const handleBlur = useCallback(
    (field: string, value: SettingsFieldInput) => {
      form.setValue(field, value);
      void form.trigger(field);
      formBlur(field as keyof T, value);
    },
    [form, formBlur]
  );

  // Total calculation using BigNumber
  const total = totalFields.reduce(
    (sum, field) =>
      new BigNumber(sum).plus((formData[field] as number) ?? 0).toNumber(),
    0
  );

  // Error accessor from RHF formState
  const getError = useCallback(
    (field: string): string | undefined => {
      const fieldError = form.formState.errors[field];
      return (fieldError as { message?: string } | undefined)?.message;
    },
    [form.formState.errors]
  );

  return {
    formData,
    handleChange,
    handleBlur,
    loading,
    error,
    retry,
    total,
    getError,
    form,
  };
}
